import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiSettings, FiClock, FiInfinity, FiTarget } = FiIcons;

function SettingsScreen({ onBack, settings, onSettingsChange }) {
  const difficulties = [
    { 
      id: 'easy', 
      label: 'Easy', 
      description: 'Tolerance: ±30 RGB values',
      color: 'from-green-500 to-emerald-600'
    },
    { 
      id: 'medium', 
      label: 'Medium', 
      description: 'Tolerance: ±20 RGB values',
      color: 'from-yellow-500 to-orange-500'
    },
    { 
      id: 'hard', 
      label: 'Hard', 
      description: 'Tolerance: ±10 RGB values',
      color: 'from-red-500 to-pink-600'
    }
  ];

  const gameModes = [
    {
      id: 'timed',
      label: 'Timed Mode',
      description: '30-second rounds with time pressure',
      icon: FiClock,
      color: 'from-blue-500 to-indigo-600'
    },
    {
      id: 'endless',
      label: 'Endless Mode',
      description: 'No time limit for relaxed play',
      icon: FiInfinity,
      color: 'from-purple-500 to-pink-600'
    }
  ];

  const updateSetting = (key, value) => {
    onSettingsChange(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onBack}
            className="bg-white/20 backdrop-blur-sm p-3 rounded-xl"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl text-white" />
          </motion.button>
          
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <SafeIcon icon={FiSettings} className="text-blue-400" />
              Settings
            </h1>
          </div>
          
          <div className="w-12" />
        </div>

        {/* Game Mode Selection */}
        <div className="mb-8">
          <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <SafeIcon icon={FiTarget} className="text-purple-400" />
            Game Mode
          </h2>
          <div className="space-y-3">
            {gameModes.map((mode) => (
              <motion.button
                key={mode.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => updateSetting('gameMode', mode.id)}
                className={`w-full p-4 rounded-2xl border-2 transition-all duration-200 ${
                  settings.gameMode === mode.id
                    ? `bg-gradient-to-r ${mode.color} border-white/30`
                    : 'bg-white/10 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${
                    settings.gameMode === mode.id ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <SafeIcon icon={mode.icon} className="text-xl text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-bold">{mode.label}</h3>
                    <p className="text-white/80 text-sm">{mode.description}</p>
                  </div>
                  {settings.gameMode === mode.id && (
                    <div className="ml-auto">
                      <div className="w-3 h-3 bg-white rounded-full" />
                    </div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Difficulty Selection */}
        <div className="mb-8">
          <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <SafeIcon icon={FiTarget} className="text-yellow-400" />
            Difficulty Level
          </h2>
          <div className="space-y-3">
            {difficulties.map((difficulty) => (
              <motion.button
                key={difficulty.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => updateSetting('difficulty', difficulty.id)}
                className={`w-full p-4 rounded-2xl border-2 transition-all duration-200 ${
                  settings.difficulty === difficulty.id
                    ? `bg-gradient-to-r ${difficulty.color} border-white/30`
                    : 'bg-white/10 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-white font-bold">{difficulty.label}</h3>
                    <p className="text-white/80 text-sm">{difficulty.description}</p>
                  </div>
                  {settings.difficulty === difficulty.id && (
                    <div className="w-3 h-3 bg-white rounded-full" />
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Game Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white/10 backdrop-blur-sm rounded-2xl p-6"
        >
          <h3 className="text-white font-bold mb-4 text-center">How to Play</h3>
          <div className="space-y-3 text-sm text-gray-300">
            <div className="flex items-start gap-3">
              <span className="text-blue-400">🎯</span>
              <p>Match the target color using RGB sliders as accurately as possible</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-green-400">⚡</span>
              <p>Quick answers in timed mode earn time bonuses</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-yellow-400">💡</span>
              <p>Use hints when stuck, but they reduce your score by 10 points</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-purple-400">🏆</span>
              <p>Build streaks for higher scores and level progression</p>
            </div>
          </div>
        </motion.div>

        {/* Current Settings Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-6 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 backdrop-blur-sm rounded-2xl p-4 border border-indigo-500/30"
        >
          <p className="text-center text-white font-medium">
            Ready to play: <span className="text-indigo-300">{settings.gameMode}</span> mode 
            on <span className="text-purple-300">{settings.difficulty}</span> difficulty
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default SettingsScreen;